import React, { useMemo } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../contexts/LanguageContext';

const InstallmentOptions = ({
    installmentOptions,
    selectedInstallment,
    onSelectInstallment,
    showInstallments,
    isLoading,
    disabled,
    amount,
    currency
}) => {
    const { th } = useLanguage();

    // Para birimi sembolü
    const currencySymbol = useMemo(() => {
        const symbols = {
            TRY: '₺',
            TL: '₺',
            USD: '$',
            EUR: '€',
            GBP: '£'
        };
        return symbols[currency] || currency || '';
    }, [currency]);

    // Tutar formatlama
    const formatAmount = (value) => {
        if (value === null || value === undefined) return '-';
        return new Intl.NumberFormat('tr-TR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    return (
        <div className="surface-card p-4 border-round-xl shadow-1 mb-4">
            <div className="flex align-items-center mb-4">
                <i className="pi pi-list text-2xl text-primary mr-3"></i>
                <h3 className="m-0 text-xl font-semibold">{th('creditCardCollection.sections.installments') || 'Taksit Seçenekleri'}</h3>
            </div>

            {/* Yükleniyor */}
            {isLoading && (
                <div className="flex justify-content-center align-items-center py-5">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                </div>
            )}

            {/* Taksit seçenekleri yok mesajı */}
            {!isLoading && !showInstallments && (
                <div className="text-center py-5">
                    <i className="pi pi-info-circle text-4xl text-500 mb-3"></i>
                    <p className="text-600 m-0">
                        {th('creditCardCollection.messages.enterCardInfoForInstallments') ||
                            'Taksit seçeneklerini görmek için tutar ve kart numarasının ilk 6 hanesini giriniz.'}
                    </p>
                </div>
            )}

            {/* Taksit seçenekleri listesi */}
            {!isLoading && showInstallments && installmentOptions.length > 0 && (
                <div className="grid">
                    {installmentOptions.map((installment) => {
                        const isSelected = selectedInstallment?.installmentCount === installment.installmentCount;

                        return (
                            <div key={installment.installmentCount} className="col-12 md:col-6 lg:col-4">
                                <div
                                    className={`border-round-xl p-3 cursor-pointer transition-all transition-duration-200 ${
                                        isSelected
                                            ? 'bg-primary text-primary-contrast border-2 border-primary'
                                            : 'surface-border border-1 hover:border-primary'
                                    }`}
                                    onClick={() => !disabled && onSelectInstallment(installment)}
                                    style={{ minHeight: '120px', opacity: disabled ? 0.6 : 1 }}
                                >
                                    {/* Başlık */}
                                    <div className="flex align-items-center justify-content-between mb-2">
                                        <span className={`font-bold text-lg ${isSelected ? '' : 'text-900'}`}>
                                            {installment.label || `${installment.installmentCount} Taksit`}
                                        </span>
                                        {isSelected && <i className="pi pi-check-circle text-xl"></i>}
                                    </div>

                                    {/* Faiz Oranı */}
                                    {installment.interestRate > 0 && (
                                        <div className={`text-sm mb-2 ${isSelected ? 'opacity-80' : 'text-500'}`}>
                                            {th('creditCardCollection.labels.interestRate') || 'Faiz Oranı'}:{' '}
                                            <span className="font-semibold">%{installment.interestRate}</span>
                                        </div>
                                    )}

                                    {/* Aylık Ödeme */}
                                    <div className={`mb-1 ${isSelected ? '' : 'text-700'}`}>
                                        <span className="text-sm">
                                            {th('creditCardCollection.labels.monthlyPayment') || 'Aylık'}:
                                        </span>
                                        <span className="font-bold text-lg ml-2">
                                            {formatAmount(installment.monthlyAmount)} {currencySymbol}
                                        </span>
                                    </div>

                                    {/* Toplam Tutar */}
                                    <div className={`${isSelected ? 'opacity-80' : 'text-500'}`}>
                                        <span className="text-sm">
                                            {th('creditCardCollection.labels.totalAmount') || 'Toplam'}:
                                        </span>
                                        <span className="font-semibold ml-2">
                                            {formatAmount(installment.totalAmount)} {currencySymbol}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Seçili taksit özeti */}
            {selectedInstallment && showInstallments && (
                <div className="mt-4 p-3 bg-primary-50 border-round-xl">
                    <div className="flex align-items-center justify-content-between flex-wrap gap-3">
                        <div>
                            <span className="text-600">{th('creditCardCollection.labels.selectedInstallment') || 'Seçilen Taksit'}:</span>
                            <span className="font-bold text-primary ml-2">{selectedInstallment.label}</span>
                        </div>
                        <div>
                            <span className="text-600">{th('creditCardCollection.labels.totalPayment') || 'Toplam Ödeme'}:</span>
                            <span className="font-bold text-primary text-xl ml-2">
                                {formatAmount(selectedInstallment.totalAmount)} {currencySymbol}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(InstallmentOptions);
